/* Batch 34 · B34-11 — the "Docs" count of a sheet row is a button that opens the row's linked document(s) in a side panel,
   without leaving the sheet. The document stays stored once (Documents tab); this panel only reads it:
   - one document → opens at once; several → a list (DOC ref, file name, type, reference, uploaded date) and the chosen one opens;
   - the same viewer as the Documents tab: PdfJsViewer on /documents/file/{id}?mode=preview for a PDF, an image inline (short-lived
     download token), any other type a Download button; the same access rules (engagement access + the file route); the row's
     documents are loaded by id (GET /vouching/{eng}/linked-documents?ids=…, PR 53 · F11 — no 2,000-document cap);
   - shown: DOC ref, file name, extracted reference, amount read (net / VAT / gross), "Open in Documents tab", Close;
   - read only: no delete / rename / re-classify; unlinking stays where it is. A count of 0 is plain text (not a button). */
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { getDownloadUrl } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDate } from "@/lib/dt";
import { fmtNumber } from "@/lib/format";
import { asArray } from "@/lib/asArray";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Download, ExternalLink, ArrowLeft, Eye } from "lucide-react";

// the PDF viewer loads only when a PDF is opened (the sheets do not pull the PDF library in)
const PdfJsViewer = React.lazy(() => import("@/components/PdfJsViewer").then((m) => ({ default: m.PdfJsViewer })));

const REF_KEYS = ["primary_reference", "invoice_number", "po_number", "grn_number", "contract_number", "document_number",
                  "voucher_number", "statement_number", "certificate_number"];

const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
};

/** The reference the server reads from a document (vouching_support.doc_ref). */
export function docReference(doc) {
  const f = doc?.extracted_fields || {};
  for (const v of [doc?.primary_reference, ...REF_KEYS.map((k) => f[k])]) {
    if (v && String(v).trim()) return String(v).trim();
  }
  return "";
}

/** S20 — the reference the row takes from this document (vouching_support.auto_ref): the extracted one, else
    "DOC-ref · file name" (``fromName`` true — shown marked "from file name"). */
export function docSupportRef(doc) {
  const r = docReference(doc);
  if (r) return { ref: r, fromName: false };
  const parts = [doc?.doc_ref, doc?.original_filename || doc?.filename].filter((v) => v && String(v).trim()).map((v) => String(v).trim());
  return { ref: parts.join(" · ") || String(doc?.id || "").slice(0, 8), fromName: true };
}

function SupportRefText({ doc, t }) {
  const { ref, fromName } = docSupportRef(doc);
  if (!ref) return "—";
  return (<><bdi>{ref}</bdi>{fromName && (
    <span className="ms-1 rounded bg-slate-100 px-1 text-[9px] text-slate-600 dark:bg-slate-800 dark:text-slate-300"
          title={t("b34m.refFromNameTitle")}>{t("b34m.refFromName")}</span>)}</>);
}

/** {net, vat, gross} as read from the document (vouching_support.doc_amounts); null when not read. */
export function docAmounts(doc) {
  const f = doc?.extracted_fields || {};
  let gross = num(f.total_amount);
  for (const k of ["grand_total", "invoice_amount", "amount"]) { if (gross === null) gross = num(f[k]); }
  let vat = num(f.vat_amount); if (vat === null) vat = num(f.tax_amount);
  const rate = num(f.vat_rate);
  let net = num(f.subtotal); if (net === null) net = num(f.net_amount);
  if (net === null && gross !== null && vat !== null) net = Math.round((gross - vat) * 100) / 100;
  if (net === null && gross !== null && rate) net = Math.round((gross / (1 + (rate > 1 ? rate / 100 : rate))) * 100) / 100;
  if (net === null && gross !== null && vat === null && !rate) net = gross;
  if (gross === null && net !== null && vat !== null) gross = Math.round((net + vat) * 100) / 100;
  return { net, vat, gross };
}

const typeLabel = (t, doc) => {
  const docType = doc?.doc_type || doc?.predicted_type || "";
  const tr = t(`docs.types.${docType}`);
  return tr && tr !== `docs.types.${docType}` ? tr : String(docType).replace(/_/g, " ").replace(/^[a-z]/, (c) => c.toUpperCase());
};

function Preview({ doc, t }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  const ct = doc.content_type || "";
  const isPdf = ct === "application/pdf";
  const isImg = ct.startsWith("image/");
  const demo = String(doc.storage_path || "").includes("demo/");
  useEffect(() => {
    let cancel = false;
    setUrl(null); setErr(false);
    if (demo || !isImg) return undefined;
    getDownloadUrl(doc.id, { mode: "preview" }).then((u) => { if (!cancel) setUrl(u); }).catch(() => { if (!cancel) setErr(true); });
    return () => { cancel = true; };
  }, [doc.id, demo, isImg]);
  const download = async () => {
    try { window.open(await getDownloadUrl(doc.id, { mode: "download" }), "_blank", "noopener"); }
    catch { setErr(true); }
  };
  if (demo) return <div className="p-6 text-center text-sm text-muted-foreground">{t("b34i.demoDoc")}</div>;
  if (isPdf) {
    return (
      <React.Suspense fallback={<div className="p-6 text-center text-sm text-muted-foreground">{t("b34i.loading")}</div>}>
        <PdfJsViewer url={`/documents/file/${doc.id}?mode=preview`} testId="vdoc-pdf-viewer"
                     downloadName={doc.original_filename || "document.pdf"} />
      </React.Suspense>
    );
  }
  if (isImg) {
    if (err) return <div className="p-6 text-center text-sm text-rose-700" data-testid="vdoc-error">{t("b34i.notAvailable")}</div>;
    return url ? <img src={url} alt={doc.original_filename || ""} className="mx-auto h-full w-full object-contain" data-testid="vdoc-image" />
      : <div className="p-6 text-center text-sm text-muted-foreground">{t("b34i.loading")}</div>;
  }
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center text-sm text-muted-foreground">
      <div>{t("b34i.noPreview")}</div>
      <button type="button" onClick={download} className="inline-flex items-center gap-1 rounded-md border px-3 py-1 text-xs hover:bg-slate-100"
              data-testid="vdoc-download"><Download className="h-3.5 w-3.5" /> {t("b34i.download")}</button>
      {err && <div className="text-rose-700">{t("b34i.downloadFailed")}</div>}
    </div>
  );
}

function DocDetails({ doc, t, lang }) {
  const a = docAmounts(doc);
  const f = (v) => (v === null ? <span className="text-muted-foreground">{t("b34i.notRead")}</span>
    : <span dir="ltr">{fmtNumber(v, lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>);
  return (
    <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs" data-testid="vdoc-details">
      <dt className="text-muted-foreground">{t("b34i.docRef")}</dt><dd data-testid="vdoc-docref"><bdi>{doc.doc_ref || "—"}</bdi></dd>
      <dt className="text-muted-foreground">{t("b34i.fileName")}</dt><dd className="break-all" data-testid="vdoc-filename"><bdi>{doc.original_filename || "—"}</bdi></dd>
      <dt className="text-muted-foreground">{t("b34i.extractedRef")}</dt><dd data-testid="vdoc-ref"><SupportRefText doc={doc} t={t} /></dd>
      <dt className="text-muted-foreground">{t("b34i.amounts")}</dt>
      <dd data-testid="vdoc-amounts">
        {t("b34i.net")} {f(a.net)} · {t("b34i.vat")} {f(a.vat)} · {t("b34i.gross")} {f(a.gross)}
      </dd>
    </dl>
  );
}

/** B35-9: true while the window is narrower than 720 px (a phone) — the panel then takes the full screen. */
const PHONE_MAX_PX = 720;
const isNarrow = () => (typeof window !== "undefined" ? window.innerWidth < PHONE_MAX_PX : false);
function useNarrowWindow() {
  const [narrow, setNarrow] = useState(isNarrow);
  useEffect(() => {
    const on = () => setNarrow(isNarrow());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return narrow;
}

/** The side panel. ``docIds`` — the row's linked document ids; ``label`` — the row's voucher / reference (header). */
export function VouchingDocViewer({ engId, docIds, label, open, onClose }) {
  const colW = useResizableColumns({ storageKey: "vouching-doc-viewer", defaults: [110, 240, 110, 160, 130], mins: [72, 130, 72, 100, 96] });   // Step 11d · add-on
  const { t, lang } = useI18n();
  const ids = asArray(docIds).filter(Boolean);
  const [docs, setDocs] = useState(null);
  const [sel, setSel] = useState(null);
  useEffect(() => {
    if (!open) { setDocs(null); setSel(null); return undefined; }
    let cancel = false;
    // PR 53 · F11 (B34-11): the linked documents BY ID (the Documents list route stops at 2,000 documents); same engagement
    // access; the file route checks row access on every read
    api.get(`/vouching/${engId}/linked-documents`, { params: { ids: ids.join(",") }, _silent: true })
      .then((r) => {
        if (cancel) return;
        const byId = Object.fromEntries(asArray(r.data).map((d) => [d.id, d]));
        const mine = ids.map((i) => byId[i] || { id: i, _missing: true });
        setDocs(mine);
        if (mine.length === 1) setSel(mine[0]);
      })
      .catch(() => { if (!cancel) setDocs(ids.map((i) => ({ id: i, _missing: true }))); });
    return () => { cancel = true; };
    // eslint-disable-next-line
  }, [open, engId, ids.join("|")]);
  const many = ids.length > 1;
  const full = useNarrowWindow();
  const side = lang === "ar" ? "left" : "right";
  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      {/* Batch 35 · B35-9: 85 % of the window width (at least 720 px), full height; full screen on a phone (window narrower than
          720 px). Arabic: opens from the left (the RTL start side). */}
      <SheetContent side={side} dir={lang === "ar" ? "rtl" : "ltr"} data-side={side} data-size={full ? "full" : "wide"}
                    style={full ? { width: "100vw", minWidth: 0, maxWidth: "100vw", height: "100%" }
                      : { width: "85vw", minWidth: 720, maxWidth: "100vw", height: "100%" }}
                    className={`flex max-w-none flex-col gap-3 sm:max-w-none ${full ? "p-3" : ""}`} data-testid="vdoc-panel">
        <SheetHeader>
          <SheetTitle>{many ? t("b34i.title") : t("b34i.titleOne")}{label ? <> · <bdi>{label}</bdi></> : null}</SheetTitle>
          <SheetDescription className="text-xs">{t("b34i.readOnly")}</SheetDescription>
        </SheetHeader>
        {!docs && <div className="text-sm text-muted-foreground">{t("b34i.loading")}</div>}
        {docs && !sel && (
          <div className="min-h-0 flex-1 overflow-auto" data-testid="vdoc-list">
            <div className="mb-2 text-xs text-muted-foreground">{t("b34i.choose")}</div>
            <ResizableTable cols={colW} className="w-full text-xs">
              <thead><tr className="text-start text-[10px] uppercase text-muted-foreground">
                <ResizableTh index={0} className="px-1 py-1 text-start">{t("b34i.docRef")}</ResizableTh><ResizableTh index={1} className="px-1 py-1 text-start">{t("b34i.fileName")}</ResizableTh>
                <ResizableTh index={2} className="px-1 py-1 text-start">{t("b34i.type")}</ResizableTh><ResizableTh index={3} className="px-1 py-1 text-start">{t("b34i.reference")}</ResizableTh>
                <ResizableTh index={4} className="px-1 py-1 text-start">{t("b34i.uploaded")}</ResizableTh>
              </tr></thead>
              <tbody>
                {docs.map((d) => (
                  <tr key={d.id} className="border-t">
                    {d._missing ? <td colSpan={5} className="px-1 py-1 text-muted-foreground">{t("b34i.notAvailable")}</td> : (<>
                      <td className="px-1 py-1">
                        <button type="button" className="text-sky-700 underline" onClick={() => setSel(d)} data-testid={`vdoc-pick-${d.id}`}>
                          <bdi>{d.doc_ref || "—"}</bdi></button>
                      </td>
                      <td className="break-all px-1 py-1"><bdi>{d.original_filename || "—"}</bdi></td>
                      <td className="px-1 py-1">{typeLabel(t, d)}</td>
                      <td className="px-1 py-1"><SupportRefText doc={d} t={t} /></td>
                      <td className="whitespace-nowrap px-1 py-1">{fmtDate(d.created_at) || "—"}</td>
                    </>)}
                  </tr>
                ))}
              </tbody>
            </ResizableTable>
          </div>
        )}
        {sel && sel._missing && <div className="text-sm text-rose-700" data-testid="vdoc-error">{t("b34i.notAvailable")}</div>}
        {sel && !sel._missing && (
          <div className="flex min-h-0 flex-1 flex-col gap-2" data-testid={`vdoc-open-${sel.id}`}>
            <DocDetails doc={sel} t={t} lang={lang} />
            <div className="min-h-0 flex-1 overflow-hidden rounded-md border bg-slate-50 dark:bg-slate-900" data-testid="vdoc-doc-area">
              <Preview doc={sel} t={t} />
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 border-t pt-2 text-xs">
          {sel && many && (
            <button type="button" className="inline-flex items-center gap-1 rounded-md border px-2 py-1" onClick={() => setSel(null)}
                    data-testid="vdoc-back"><ArrowLeft className="h-3.5 w-3.5 rtl:rotate-180" /> {t("b34i.back")}</button>
          )}
          {sel && !sel._missing && (
            <Link to={`/documents?highlight=${sel.id}`} className="inline-flex items-center gap-1 text-sky-700 hover:underline"
                  data-testid="vdoc-open-in-documents"><ExternalLink className="h-3.5 w-3.5" /> {t("b34i.openInDocuments")}</Link>
          )}
          <button type="button" className="ms-auto rounded-md border px-3 py-1" onClick={onClose} data-testid="vdoc-close">{t("b34i.close")}</button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/** The "Docs" cell content: plain "0", else a button with the count that opens the panel. */
export function LinkedDocsCount({ engId, docIds, label, testId, className = "" }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const n = asArray(docIds).filter(Boolean).length;
  if (!n || !engId) return <span data-testid={testId}>{n}</span>;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} title={t("b34i.openDocs")} aria-label={`${t("b34i.openDocs")} (${n})`}
              className={`rounded px-1 text-sky-700 underline hover:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-sky-400 dark:text-sky-300 dark:hover:bg-sky-950/40 ${className}`}
              data-testid={testId}>{n}</button>
      {open && <VouchingDocViewer engId={engId} docIds={docIds} label={label} open={open} onClose={() => setOpen(false)} />}
    </>
  );
}

/** Next to a count that already is the "link documents" button (SURL rows): a small "open" button, none when nothing is linked. */
export function LinkedDocsViewButton({ engId, docIds, label, testId }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const n = asArray(docIds).filter(Boolean).length;
  if (!n || !engId) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} title={t("b34i.openDocs")} aria-label={t("b34i.openDocs")}
              className="ms-1 inline-flex items-center rounded p-0.5 text-sky-700 hover:bg-sky-50 focus:outline-none focus:ring-2 focus:ring-sky-400"
              data-testid={testId}><Eye className="h-3.5 w-3.5" /></button>
      {open && <VouchingDocViewer engId={engId} docIds={docIds} label={label} open={open} onClose={() => setOpen(false)} />}
    </>
  );
}

export default VouchingDocViewer;
